import { Injectable, signal } from '@angular/core';
import type { LoginContext, LoginVerdict } from '@cdw-ae/common-data';

/**
 * Screen states for the login-status page — the backend login verdicts.
 */
export interface LoginScreenState {
  verdict: LoginVerdict;
  context: LoginContext | null;
  /**
   * First entry into the app (EDUS-2852). Optional here, unlike on the wire
   * type: the login-status screen builds this shape in tests without caring,
   * and absent means the same as false to the only reader
   * (`resolvePostAuthTarget`).
   */
  firstEntry?: boolean;
}

/**
 * Hands the last login resolution from UserIdentityService to whoever needs
 * it: the login-status screen (which verdict to render, and the copy facts
 * that go with it) and, since EDUS-2810, the app footer's licence-expiry line
 * on the APP verdict.
 *
 * Deliberately not NgRx: it is set once per resolution and read as a signal,
 * with no actions, reducers or effects worth the ceremony. It resets to null
 * on a hard refresh, which is safe because `resolve-login` re-runs on every
 * `onIdTokenChanged` — consumers must tolerate a null for the one round trip
 * that takes (the footer hides its line; the login-status screen bounces to
 * /welcome).
 */
@Injectable({ providedIn: 'root' })
export class LoginVerdictStateService {
  readonly state = signal<LoginScreenState | null>(null);

  /**
   * Record a login resolution. Production code writes through here rather than
   * setting {@link state} directly, because `firstEntry` must LATCH.
   *
   * `resolve-login` runs on every `onIdTokenChanged`, and that listener fires
   * more than once per sign-in: the APP branch of `routeByVerdict`
   * force-refreshes the ID token (and the auth interceptor does the same on a
   * 401), each refresh re-firing the listener outside the `isValidatingClaims`
   * guard, which only covers re-entry *during* a run. So one sign-in produces
   * several resolutions. The backend computes `firstEntry` from
   * `users.first_login_at` BEFORE stamping it, so the FIRST of those answers
   * true and every later one correctly answers false.
   *
   * Writing each resolution straight onto the signal therefore overwrote the
   * single true with a false — and `resolvePostAuthTarget` reads the flag live,
   * once per `setUser` dispatch, so by the time it looked the first entry was
   * gone and the user landed on the dashboard instead of `/tour` (EDUS-2852).
   * The invited-user journey only kept working because its CTA carries a
   * `redirectUrl` of `/tour`.
   *
   * `firstEntry` is a fact about the sign-in, not about the individual HTTP
   * call, so a later resolution may raise it but never lower it. Everything
   * else on the resolution (verdict, context) is still last-write-wins — those
   * ARE per-call facts, and the footer's expiry line depends on the refresh.
   */
  recordResolution(resolution: LoginScreenState): void {
    this.state.set({
      ...resolution,
      firstEntry:
        resolution.firstEntry === true || this.state()?.firstEntry === true,
    });
  }

  /**
   * The latch has done its job: the user has actually been landed on the tour.
   *
   * It has to be released, because the post-auth navigation re-runs on every
   * `setUser` dispatch — including the one behind Firebase's ~hourly
   * background token refresh. First entry outranks every other routing rule,
   * so a latch left true for the life of the tab would yank a user an hour
   * into their work back to `/tour`.
   *
   * Release it AFTER the navigation resolves rather than when the target is
   * computed: a duplicate resolution arriving while that navigation is still
   * in flight (the `/tour` route is lazy-loaded, so the window is real) then
   * still reads true and re-targets `/tour`, instead of racing the first
   * navigation back to the dashboard — which is the failure this whole latch
   * exists to prevent.
   */
  firstEntryHandled(): void {
    const current = this.state();
    if (current?.firstEntry) {
      this.state.set({ ...current, firstEntry: false });
    }
  }
}
